import StyledInput from '@/components/styled/StyledInput';
import { YesNoStyledRadioGroup } from '@/components/styled/StyledRadioGroup';
import { Label } from '@/components/ui/label';
import { useCallback } from 'react';
import {
  Control,
  useController,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from '@/i18n';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';

interface HotelRoomTarifCardDefaultPricesProps {
  control: Control<HotelFormDataWithTranslations>;
  index: number;
  tarifIndex: number;
  isViewMode?: boolean;
  pricingType?: 'byPerson' | 'byRoom';
  priceReductionType?: 'newPrice' | 'reduction';
}

const HotelRoomTarifCardDefaultPrices = ({
  control,
  index,
  tarifIndex,
  isViewMode = false,
  pricingType = 'byPerson',
  priceReductionType = 'newPrice',
}: HotelRoomTarifCardDefaultPricesProps) => {
  const { t } = useTranslation();

  const acceptsChildPricing = useWatch({
    control,
    name: 'acceptsChildPricing',
  });
  const childAges = useWatch({ control, name: 'childAges' });

  const defaultPricesExist = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPricesExist`,
  });

  const defaultPrices = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPrices`,
  });

  const {
    field: { onChange: onChangePrice },
  } = useController({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPrices.price`,
  });

  const {
    field: { onChange: onChangeFirstSpecialPrice },
  } = useController({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPrices.firstSpecialPrice`,
  });

  const {
    field: { onChange: onChangeSecondSpecialPrice },
  } = useController({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPrices.secondSpecialPrice`,
  });

  const { setValue } = useFormContext();

  const pricePartitions = useWatch({
    control,
    name: `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions`,
  });

  const updatePrices = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      price: 'price' | 'firstSpecialPrice' | 'secondSpecialPrice',
    ) => {
      switch (price) {
        case 'price':
          onChangePrice(e);
          break;
        case 'firstSpecialPrice':
          onChangeFirstSpecialPrice(e);
          break;
        case 'secondSpecialPrice':
          onChangeSecondSpecialPrice(e);
          break;
      }
      if (pricingType === 'byRoom') {
        pricePartitions?.individual?.forEach((_, indexPartition) => {
          setValue(
            `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.individual.${indexPartition}.${price}`,
            e.target.value,
          );
        });
      }
      pricePartitions?.double?.forEach((_, indexPartition) => {
        setValue(
          `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.double.${indexPartition}.${price}`,
          e.target.value,
        );
      });
      pricePartitions?.triple?.forEach((_, indexPartition) => {
        setValue(
          `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.triple.${indexPartition}.${price}`,
          e.target.value,
        );
      });
      pricePartitions?.quadruple?.forEach((_, indexPartition) => {
        setValue(
          `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions.quadruple.${indexPartition}.${price}`,
          e.target.value,
        );
      });
    },
    [
      index,
      onChangeFirstSpecialPrice,
      onChangePrice,
      onChangeSecondSpecialPrice,
      pricePartitions?.double,
      pricePartitions?.individual,
      pricePartitions?.quadruple,
      pricePartitions?.triple,
      pricingType,
      setValue,
      tarifIndex,
    ],
  );

  const updateAdultPrices = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => updatePrices(e, 'price'),
    [updatePrices],
  );

  const updateFirstSpecialPrices = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      updatePrices(e, 'firstSpecialPrice'),
    [updatePrices],
  );

  const updateSecondSpecialPrices = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      updatePrices(e, 'secondSpecialPrice'),
    [updatePrices],
  );

  return (
    <>
      <Label className="text-gray-700">
        {t('hotels.roomTarifs.defaultPrices.identicalTarifsQuestion')}
      </Label>
      <YesNoStyledRadioGroup
        control={control}
        name={`hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPricesExist`}
        disabled={isViewMode}
      />
      {defaultPricesExist === 'true' && (
        <>
          <Label className="text-gray-700">
            {t('hotels.roomTarifs.defaultPrices.copyTarifsMessage')}
          </Label>
          <div className="flex justify-center gap-6">
            <div className="w-40">
              <StyledInput
                control={control}
                name={`hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPrices.price`}
                type="number"
                min={0}
                title={
                  pricingType === 'byPerson'
                    ? t('hotels.roomTarifs.defaultPrices.adultRate')
                    : t('hotels.roomTarifs.defaultPrices.totalRoom')
                }
                placeholder="Ex : 100"
                disabled={isViewMode}
                onChange={updateAdultPrices}
                hideNumberFieldArrows
                compareValue={{
                  color: 'purple',
                  value: defaultPrices?.price?.toString(),
                }}
              />
            </div>
            {pricingType === 'byPerson' &&
              acceptsChildPricing === 'true' &&
              childAges &&
              childAges.length === 2 && (
                <div className="w-40">
                  <StyledInput
                    control={control}
                    name={`hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPrices.secondSpecialPrice`}
                    type="number"
                    min={0}
                    title={
                      priceReductionType === 'newPrice'
                        ? t('hotels.roomTarifs.defaultPrices.childRate', {
                            ageFrom: childAges[1].childAgeFrom,
                            ageTo: childAges[1].childAgeTo,
                          })
                        : t('hotels.roomTarifs.defaultPrices.childReduction', {
                            ageFrom: childAges[1].childAgeFrom,
                            ageTo: childAges[1].childAgeTo,
                          })
                    }
                    titleClassName={
                      priceReductionType === 'reduction'
                        ? 'text-success-700'
                        : undefined
                    }
                    placeholder="Ex : 50"
                    disabled={isViewMode}
                    onChange={updateSecondSpecialPrices}
                    hideNumberFieldArrows
                    compareValue={{
                      color: 'success',
                      value: defaultPrices?.secondSpecialPrice?.toString(),
                    }}
                  />
                </div>
              )}
            {pricingType === 'byPerson' &&
              acceptsChildPricing === 'true' &&
              childAges &&
              childAges.length > 0 && (
                <div className="w-40">
                  <StyledInput
                    control={control}
                    name={`hotelRoomCategories.${index}.tarifs.${tarifIndex}.defaultPrices.firstSpecialPrice`}
                    type="number"
                    min={0}
                    title={
                      priceReductionType === 'newPrice'
                        ? t('hotels.roomTarifs.defaultPrices.childRate', {
                            ageFrom: childAges[0].childAgeFrom,
                            ageTo: childAges[0].childAgeTo,
                          })
                        : t('hotels.roomTarifs.defaultPrices.childReduction', {
                            ageFrom: childAges[0].childAgeFrom,
                            ageTo: childAges[0].childAgeTo,
                          })
                    }
                    titleClassName={
                      priceReductionType === 'reduction'
                        ? 'text-success-700'
                        : undefined
                    }
                    placeholder="Ex : 50"
                    disabled={isViewMode}
                    onChange={(e) => {
                      updateFirstSpecialPrices(e);
                      if (childAges.length === 1) updateSecondSpecialPrices(e);
                    }}
                    hideNumberFieldArrows
                    compareValue={{
                      color: 'warning',
                      value: defaultPrices?.firstSpecialPrice?.toString(),
                    }}
                  />
                </div>
              )}
          </div>
        </>
      )}
    </>
  );
};

export default HotelRoomTarifCardDefaultPrices;
